import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dropbox Chooser App Key - loaded dynamically
const DROPBOX_APP_KEY = process.env.REACT_APP_DROPBOX_APP_KEY || '';

const DropboxContext = createContext(null);

export const useDropbox = () => {
  const context = useContext(DropboxContext);
  if (!context) {
    throw new Error('useDropbox must be used within DropboxProvider');
  }
  return context;
};

export const DropboxProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accountEmail, setAccountEmail] = useState(null);
  const [isChooserLoaded, setIsChooserLoaded] = useState(false);

  // Load Dropbox Chooser SDK
  useEffect(() => {
    if (!DROPBOX_APP_KEY) {
      setIsLoading(false);
      return;
    }

    // Check if script already exists
    if (window.Dropbox) {
      setIsChooserLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.id = 'dropboxjs';
    script.setAttribute('data-app-key', DROPBOX_APP_KEY);
    script.async = true;
    
    script.onload = () => {
      setIsChooserLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Dropbox Chooser SDK');
    };

    document.head.appendChild(script);

    return () => {
      // Don't remove script on unmount to avoid reloading
    };
  }, []);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('tapInToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/dropbox/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setIsConnected(data.connected);
        setAccountEmail(data.account_email);
      }
    } catch (error) {
      console.error('Failed to check Dropbox status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const connect = useCallback(async () => {
    const token = localStorage.getItem('tapInToken');
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${API_URL}/api/dropbox/connect`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to initiate Dropbox connection';
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      
      if (data.authorize_url) {
        // Use replace for more reliable mobile redirect
        window.location.replace(data.authorize_url);
        return { success: true, redirecting: true };
      }
      
      return { success: false, error: 'No authorization URL received' };
    } catch (error) {
      console.error('Dropbox connect error:', error);
      return { success: false, error: error.message || 'Connection failed' };
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      const token = localStorage.getItem('tapInToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/api/dropbox/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setIsConnected(false);
        setAccountEmail(null);
        return { success: true };
      }
      
      throw new Error('Failed to disconnect');
    } catch (error) {
      console.error('Dropbox disconnect error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const openChooser = useCallback((options = {}) => {
    return new Promise((resolve, reject) => {
      if (!window.Dropbox) {
        reject(new Error('Dropbox Chooser not loaded'));
        return;
      }

      const defaultOptions = {
        success: (files) => {
          resolve(files);
        },
        cancel: () => {
          resolve(null); // User cancelled
        },
        linkType: 'preview', // 'preview' or 'direct'
        multiselect: true,
        extensions: [
          '.pdf', '.doc', '.docx', '.txt',
          '.xls', '.xlsx', '.csv',
          '.ppt', '.pptx',
          '.jpg', '.jpeg', '.png', '.gif', '.webp',
          '.mp4', '.mov', '.avi',
          '.mp3', '.wav', '.m4a',
          '.zip', '.rar'
        ],
        folderselect: false,
        sizeLimit: 100 * 1024 * 1024, // 100MB
      };

      window.Dropbox.choose({
        ...defaultOptions,
        ...options
      });
    });
  }, []);

  const shareFiles = useCallback(async (conversationId, files, receiverId = null) => {
    try {
      const token = localStorage.getItem('tapInToken');
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      if (!files || files.length === 0) {
        throw new Error('No files selected to share.');
      }

      // Need either conversationId or receiverId
      if (!conversationId && !receiverId) {
        throw new Error('Unable to determine recipient.');
      }

      const formattedFiles = files.map(f => ({
        file_id: f.id,
        name: f.name,
        link: f.link,
        size: f.bytes,
        icon: f.icon,
        thumbnail: f.thumbnailLink || null
      }));

      console.log('Sharing files:', { conversationId, receiverId, fileCount: files.length });

      const requestBody = {
        files: formattedFiles
      };
      
      // Send conversation_id if available, otherwise receiver_id
      if (conversationId) {
        requestBody.conversation_id = conversationId;
      }
      if (receiverId) {
        requestBody.receiver_id = receiverId;
      }

      const response = await fetch(`${API_URL}/api/dropbox/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Share API error:', response.status, errorData);
        
        // Handle specific error codes
        if (response.status === 403) {
          throw new Error('Please connect your Dropbox account first.');
        } else if (response.status === 404) {
          throw new Error('Conversation not found. Please try again.');
        } else if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        
        const errorMessage = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : (errorData.detail?.message || `Failed to share files (Error ${response.status})`);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Files shared successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Share files error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const value = {
    isConnected,
    isLoading,
    accountEmail,
    isChooserLoaded,
    isConfigured: !!DROPBOX_APP_KEY,
    connect,
    disconnect,
    openChooser,
    shareFiles,
    refreshStatus: checkConnectionStatus
  };

  return (
    <DropboxContext.Provider value={value}>
      {children}
    </DropboxContext.Provider>
  );
};

export default DropboxContext;
